import axios from 'axios';
import { SchedulingLogsResponse } from '../types/types';

// Use relative '/api' in production (Docker behind Nginx proxy), fallback to localhost in dev
// Backend server.port is 8082 with context-path '/api'
// In development, use the current hostname to support access from mobile devices on the same network
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : `http://${window.location.hostname}:8081/api`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor per aggiungere l'username alle richieste
api.interceptors.request.use(
  (config) => {
    const path = window.location.pathname;
    const source = document.documentElement.dataset.turnoSource || (path === '/generazione-orario' ? 'GENERAZIONE_ORARI'
      : path === '/mio-orario' ? 'MIO_ORARIO'
      : path.startsWith('/orario-dipendente') ? 'ORARIO_DIPENDENTE'
      : 'API_DIRECT');
    config.headers['X-Turno-Source'] = source;
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      const user = JSON.parse(userInfo);
      // Usa username, altrimenti fallback a nome per massima compatibilità
      const xUser = user.username || user.nome;
      if (xUser) {
        config.headers['X-Username'] = xUser;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor per gestire le risposte e errori
api.interceptors.response.use(
  (response) => {
    // Invalida cache coordinatore lock dopo salvataggio turni/orari
    const method = response.config.method?.toUpperCase() || '';
    const url = response.config.url || '';
    if ((method === 'PUT' || method === 'POST' || method === 'DELETE') && (url.includes('/orari-generati') || url.includes('/turni'))) {
      try {
        console.log('[API Interceptor] Salvataggio rilevato -', method, url, '- invalido cache');
        localStorage.removeItem('apporari-coordinatore-ore-lock-cache');
        // Emetti evento per notificare che cache è stata invalidata
        window.dispatchEvent(new CustomEvent('coordinatore-ore-lock-cache-invalidated', {
          detail: { method, url }
        }));
      } catch {
        // ignore
      }
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Utente non autenticato, redirect al login
      localStorage.removeItem('authToken');
      localStorage.removeItem('userInfo');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Helper function for making API requests
export const apiRequest = async <T = any>(
  endpoint: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: string;
    headers?: Record<string, string>;
  } = {}
): Promise<T> => {
  const { method = 'GET', body, headers = {} } = options;
  
  // Determina se è form-encoded data
  const isFormEncoded = headers['Content-Type'] === 'application/x-www-form-urlencoded';
  
  const config = {
    method,
    url: endpoint,
    data: body ? (isFormEncoded ? body : JSON.parse(body)) : undefined,
    headers: {
      'Content-Type': isFormEncoded ? 'application/x-www-form-urlencoded' : 'application/json',
      ...headers,
    },
  };

  const response = await api(config);
  return response.data;
};

/**
 * Recupera i log strutturati delle fasi domenicale e sabatale
 * senza generare turni. Usato dal pannello Log Pianificazione.
 */
export const fetchSchedulingLogs = async (
  negozio: string,
  mese: string,
  anno: number,
  dipendentiAttivi?: string[],
  neoassunte?: string[]
): Promise<SchedulingLogsResponse> => {
  const response = await api.post<SchedulingLogsResponse>(
    `/orari-generati/scheduling-logs`,
    { negozio, mese, anno, dipendentiAttivi: dipendentiAttivi ?? [], neoassunte: neoassunte ?? [] }
  );
  return response.data;
};
